import { useId } from 'react';
import type { Bottle } from '../../shared/api';

interface BottleGaugeProps {
  bottle: Pick<Bottle, 'kind' | 'level' | 'quantity'>;
  size?: 'small' | 'large';
}

// Drawn in a 40 x 100 box: a neck, shoulders and a straight body.
const OUTLINE = 'M15 2h10v16c0 4 11 6 11 14v62a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V32c0-8 11-10 11-14z';
const BODY_TOP = 32;
const BODY_BOTTOM = 98;

/** A bottle silhouette filled to the estimated level; quantity > 1 is shown as a count. */
export function BottleGauge({ bottle, size = 'small' }: BottleGaugeProps) {
  const clipId = useId();
  const level = bottle.quantity === 0 ? 0 : Math.min(1, Math.max(0, bottle.level));
  const fillTop = BODY_BOTTOM - (BODY_BOTTOM - BODY_TOP) * level;
  const percent = Math.round(level * 100);
  const label = bottle.quantity === 0 ? 'Finished' : `${percent}% left${bottle.quantity > 1 ? `, ${bottle.quantity} on hand` : ''}`;

  return (
    <span className={`bottle-gauge bottle-gauge--${size}`} data-kind={bottle.kind} role="img" aria-label={label}>
      <svg viewBox="0 0 40 100" aria-hidden="true">
        <defs>
          <clipPath id={clipId}>
            <path d={OUTLINE} />
          </clipPath>
        </defs>
        <path className="bottle-gauge__glass" d={OUTLINE} />
        <rect className="bottle-gauge__fill" clipPath={`url(#${clipId})`} x="0" y={fillTop} width="40" height={100 - fillTop} />
        <path className="bottle-gauge__outline" d={OUTLINE} />
      </svg>
      {bottle.quantity > 1 && <span className="bottle-gauge__count">×{bottle.quantity}</span>}
    </span>
  );
}
